import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Search, Download, MapPin, Filter, X, ExternalLink, MapPinned, Loader2, Send, Mail, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { VenueDetailsModal } from "./components/modals/VenueDetailsModal";
import { EmailModal } from "./components/modals/EmailModal";
import { useVenues } from "@/hooks/useVenues";
import Supercluster from "supercluster";
import { VENUE_TYPE_COLORS } from "./constants/venueTypes";
import { calculateDistanceMiles } from "./lib/distance";
import { makeClusterDiv } from "./map/makeClusterDiv";
import { makeVenuePinDiv } from "./map/markerDom";
import { openSharedVenueInfoWindow } from "./map/infoWindowDom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function AppOld() {
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("All Types");
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [radiusFilter, setRadiusFilter] = useState(100); // miles
  const [centerLocation, setCenterLocation] = useState(null); // {lat, lng, name}
  const [showRadiusFilter, setShowRadiusFilter] = useState(false);
  const [selectedVenues, setSelectedVenues] = useState(new Set());
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const searchInputRef = useRef(null);
  const radiusCircleRef = useRef(null);
  const { venues, loading, refresh } = useVenues();
  const DETAIL_ZOOM = 8;
  const clusterIndexRef = useRef(null);
  const renderedMarkersRef = useRef(new Map());
  const sharedInfoWindowRef = useRef(null);

  let mapsLoaderPromise;

  function loadGoogleMaps(apiKey) {
    if (mapsLoaderPromise) return mapsLoaderPromise;

    mapsLoaderPromise = new Promise((resolve, reject) => {
      // already ready
      if (window.google?.maps?.Map || window.google?.maps?.importLibrary) {
        resolve(window.google);
        return;
      }

      // if script already exists, just wait for readiness
      const existing = document.getElementById("google-maps-js");
      if (existing) {
        waitForMapsReady(resolve, reject);
        return;
      }

      const script = document.createElement("script");
      script.id = "google-maps-js";

      // IMPORTANT: do NOT include libraries=marker when using importLibrary
      // also: keep loading=async but do not rely on onload
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&loading=async`;
      script.async = true;

      script.onerror = () => reject(new Error("Google Maps script failed to load"));
      script.onload = () => {
        // don't trust onload; wait for actual API readiness
        waitForMapsReady(resolve, reject);
      };

      document.head.appendChild(script);
    });

    return mapsLoaderPromise;
  }

  function waitForMapsReady(resolve, reject) {
    const start = Date.now();
    const timeoutMs = 10000;

    const tick = () => {
      const g = window.google;
      if (g?.maps?.importLibrary || g?.maps?.Map) {
        resolve(g);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(new Error("Google Maps API loaded but never became ready (no Map/importLibrary)"));
        return;
      }
      requestAnimationFrame(tick);
    };

    tick();
  }

  const initMap = async () => {
    if (!mapRef.current || googleMapRef.current) return;

    try {
      // Prefer modern loader if available
      if (window.google.maps.importLibrary) {
        const { Map } = await window.google.maps.importLibrary("maps");
        // marker library only if you use AdvancedMarkerElement
        await window.google.maps.importLibrary("marker");

        const map = new Map(mapRef.current, {
          center: { lat: 37.0902, lng: -95.7129 },
          zoom: 4,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          mapId: "DOLPHIN_DIGITAL_MAP",
        });

        googleMapRef.current = map;
        setMapLoaded(true);
        return;
      }

      // Fallback (older flow or partial load)
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 37.0902, lng: -95.7129 },
        zoom: 4,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        mapId: "DOLPHIN_DIGITAL_MAP",
      });

      googleMapRef.current = map;
      setMapLoaded(true);
    } catch (e) {
      console.error("Error initializing map:", e);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadGoogleMaps(GOOGLE_MAPS_API_KEY);
        if (cancelled) return;
        await initMap();
      } catch (e) {
        console.error("Error loading Google Maps:", e);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!filteredVenues?.length) return;

    const points = filteredVenues.map((v) => ({
      type: "Feature",
      properties: {
        cluster: false,
        venueId: v.venue_id,
        venue: v,
      },
      geometry: {
        type: "Point",
        coordinates: [v.longitude, v.latitude], // [lng, lat]
      },
    }));

    clusterIndexRef.current = new Supercluster({
      radius: 60,
      maxZoom: 18,
    }).load(points);
  }, [filteredVenues]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!mapLoaded || !map || !window.google || !clusterIndexRef.current) return;

    if (!sharedInfoWindowRef.current) {
      sharedInfoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const clearRenderedMarkers = () => {
      renderedMarkersRef.current.forEach((m) => {
        try { m.map = null; } catch {}
      });
      renderedMarkersRef.current.clear();
    };

    const renderViewport = () => {
      const index = clusterIndexRef.current;
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (!bounds || zoom == null || !index) return;
      clearRenderedMarkers();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const bbox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
      const features = index.getClusters(bbox, Math.round(zoom));

      // show clusters only at low zoom
    // show clusters at low zoom, but ALSO show any unclustered single points
    if (zoom < DETAIL_ZOOM) {
      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;

        // cluster
        if (f.properties.cluster) {
          const count = f.properties.point_count;
          const clusterId = f.id;

          const div = makeClusterDiv(count);
          div.addEventListener("click", () => {
            const maxZoom = map.maxZoom ?? 21;
            const exp = index.getClusterExpansionZoom(clusterId);
            const nextZoom = Math.min(exp + 1, maxZoom);
            map.panTo({ lat, lng });
            map.setZoom(nextZoom);
          });

          const m = new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position: { lat, lng },
            content: div,
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
          });

          renderedMarkersRef.current.set(`c_${clusterId}`, m);
          continue;
        }

        // singleton point (not clustered) -> show it even at low zoom
        const venue = f.properties.venue;
        if (!venue) continue;

        const content = makeVenuePinDiv(venue);
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: venue.latitude, lng: venue.longitude },
          content,
          title: venue.venue_name,
        });

        marker.addListener("click", () => {
          const color = VENUE_TYPE_COLORS[venue.venue_type] || VENUE_TYPE_COLORS["Other"];
          openSharedVenueInfoWindow({
            sharedInfoWindow: sharedInfoWindowRef.current,
            map,
            anchor: marker,
            venue,
            color,
            onViewDetails: () => {
              setSelectedVenue(venue);
              setShowDetailsModal(true);
              sharedInfoWindowRef.current?.close();
            },
          });
        });

        content.addEventListener("dblclick", (e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectedVenue(venue);
          setShowDetailsModal(true);
          sharedInfoWindowRef.current?.close();
        });

        renderedMarkersRef.current.set(`v_${venue.venue_id}`, marker);
      }
      return;
    }

      // at high zoom show points (and any remaining clusters)
      for (const f of features) {
        const [lng, lat] = f.geometry.coordinates;

        if (f.properties.cluster) {
          const count = f.properties.point_count;
          const clusterId = f.id;
          const div = makeClusterDiv(count);
          div.addEventListener("click", () => {
            const maxZoom = map.maxZoom ?? 21;          // or map.get("maxZoom") ?? 21
            const exp = index.getClusterExpansionZoom(clusterId);
            const nextZoom = Math.min(exp + 1, maxZoom);
            map.panTo({ lat, lng });
            map.setZoom(nextZoom);
          });

          const m = new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position: { lat, lng },
            content: div,
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
          });

          renderedMarkersRef.current.set(`c_${clusterId}`, m);
          continue;
        }

        const venue = f.properties.venue;
        if (!venue) continue;

        const content = makeVenuePinDiv(venue);

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: venue.latitude, lng: venue.longitude },
          content,
          title: venue.venue_name,
        });

        // --- single click: open info window (preview) ---
        marker.addListener("click", () => {
          const color = VENUE_TYPE_COLORS[venue.venue_type] || VENUE_TYPE_COLORS["Other"];

          openSharedVenueInfoWindow({
            sharedInfoWindow: sharedInfoWindowRef.current,
            map,
            anchor: marker,
            venue,
            color,
            onViewDetails: () => {
              setSelectedVenue(venue);
              setShowDetailsModal(true);
              sharedInfoWindowRef.current?.close();
            },
          });
        });

        // double click on DOM content (reliable)
        content.addEventListener("dblclick", (e) => {
          e.preventDefault();
          e.stopPropagation();
          setSelectedVenue(venue);
          setShowDetailsModal(true);
          sharedInfoWindowRef.current?.close();
        });

        renderedMarkersRef.current.set(`v_${venue.venue_id}`, marker);
      }
    };

    // initial render + update after pan/zoom ends
    renderViewport();
    const idleListener = map.addListener("idle", renderViewport);

    return () => {
      if (idleListener) idleListener.remove();
      sharedInfoWindowRef.current?.close();
      clearRenderedMarkers();
    };
  }, [mapLoaded, filteredVenues.length]);


  // Generate search suggestions
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const uniqueSuggestions = new Map(); // Use Map to prevent duplicates with normalized keys
    
    // Collect matching store names, cities, states, and zipcodes
    venues.forEach(venue => {
      // Match store names - prioritize exact matches
      if (venue.store_name && venue.store_name.toLowerCase().includes(query)) {
        const normalizedKey = `store_${venue.venue_name.toLowerCase()}`;
        if (!uniqueSuggestions.has(normalizedKey)) {
          uniqueSuggestions.set(normalizedKey, { 
            type: 'store', 
            value: venue.store_name,
            venueId: venue.venue_name,
            lat: venue.latitude,
            lng: venue.longitude,
            display: venue.store_name,
            subdisplay: `${venue.city}, ${venue.state}`
          });
        }
      }
      
      // Match cities - normalize to prevent duplicates
      if (venue.city && venue.city.toLowerCase().includes(query)) {
        const normalizedKey = `city_${venue.city.toLowerCase()}_${venue.state.toLowerCase()}`;
        if (!uniqueSuggestions.has(normalizedKey)) {
          uniqueSuggestions.set(normalizedKey, { 
            type: 'city', 
            value: venue.city.toLowerCase(), 
            state: venue.state,
            display: `${venue.city}, ${venue.state}`
          });
        }
      }
      
      // Match states - normalize to prevent duplicates
      if (venue.state && venue.state.toLowerCase().includes(query)) {
        const normalizedKey = `state_${venue.state.toLowerCase()}`;
        if (!uniqueSuggestions.has(normalizedKey)) {
          uniqueSuggestions.set(normalizedKey, { 
            type: 'state', 
            value: venue.state.toLowerCase(),
            display: venue.state
          });
        }
      }
      
      // Match zipcodes - use startsWith for better zipcode matching
      if (venue.zipcode && venue.zipcode.startsWith(query)) {
        const normalizedKey = `zipcode_${venue.zipcode}`;
        if (!uniqueSuggestions.has(normalizedKey)) {
          uniqueSuggestions.set(normalizedKey, { 
            type: 'zipcode', 
            value: venue.zipcode,
            display: `ZIP: ${venue.zipcode}`
          });
        }
      }
    });

    // Convert to array, sort (states/cities/zipcodes first, then stores), and limit to 10 suggestions
    const suggestionArray = Array.from(uniqueSuggestions.values())
      .sort((a, b) => {
        // Sort order: state -> city -> zipcode -> store (locations before stores)
        const order = { state: 0, city: 1, zipcode: 2, store: 3 };
        return order[a.type] - order[b.type];
      })
      .slice(0, 10);
    
    setSearchSuggestions(suggestionArray);
    setShowSuggestions(suggestionArray.length > 0);
  }, [searchQuery, venues]);

  // Filter venues with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let filtered = venues;

      if (selectedType && selectedType !== "All Types") {
        filtered = filtered.filter(v => v.venue_type === selectedType);
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(v => 
          v.city.toLowerCase().includes(query) ||
          v.state.toLowerCase().includes(query) ||
          v.zipcode.includes(query) ||
          v.venue_name.toLowerCase().includes(query) ||
          (v.store_name && v.store_name.toLowerCase().includes(query))
        );
      }

      // Apply radius filter if enabled
      if (showRadiusFilter && centerLocation) {
        filtered = filtered.filter(v => {
          const distance = calculateDistanceMiles(
            centerLocation.lat,
            centerLocation.lng,
            v.latitude,
            v.longitude
          );
          return distance <= radiusFilter;
        });
      }

      setFilteredVenues(filtered);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedType, venues, showRadiusFilter, centerLocation, radiusFilter]);

  const handleVenueClick = (venue) => {
    if (!googleMapRef.current) return;
    const maxZoom = googleMapRef.current.maxZoom ?? 21;
    googleMapRef.current.setCenter({ lat: venue.latitude, lng: venue.longitude });
    googleMapRef.current.setZoom(Math.min(Math.max(DETAIL_ZOOM, 19), maxZoom));
    setSelectedVenue(venue);
    setShowDetailsModal(true);
  };

  const handleVenueDetails = (venue, e) => {
    e.stopPropagation();
    setSelectedVenue(venue);
    setShowDetailsModal(true);
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);

    // Handle store name selection - zoom directly to that venue
    if (suggestion.type === 'store') {
      setSearchQuery(suggestion.value);
      const venue = venues.find(v => v.venue_name === suggestion.venueId);
      if (venue && googleMapRef.current) {
        // Zoom to the specific venue
        googleMapRef.current.setCenter({ lat: venue.latitude, lng: venue.longitude });
        googleMapRef.current.setZoom(16);
        const key = `v_${venue.venue_id}`;
        const m = renderedMarkersRef.current.get(key);
        if (m?.content) {
          setTimeout(() => m.content.click(), 0);
        }
      }
    }
    // Enable radius filter when clicking on a city, state, or zipcode
    else if (suggestion.type === 'city' || suggestion.type === 'state' || suggestion.type === 'zipcode') {
      // Clear text search so radius filter alone determines results —
      // otherwise only venues matching the city/state name would appear,
      // excluding nearby venues in other cities within the radius.
      setSearchQuery("");

      // Find a venue in that location to get coordinates
      const locationVenue = venues.find(v =>
        (suggestion.type === 'city' && v.city.toLowerCase() === suggestion.value.toLowerCase() && v.state?.toLowerCase() === suggestion.state?.toLowerCase()) ||
        (suggestion.type === 'state' && v.state.toLowerCase() === suggestion.value.toLowerCase()) ||
        (suggestion.type === 'zipcode' && v.zipcode === suggestion.value)
      );
      
      if (locationVenue) {
        setCenterLocation({
          lat: locationVenue.latitude,
          lng: locationVenue.longitude,
          name: suggestion.display
        });
        setShowRadiusFilter(true);
        setRadiusFilter(50); // Default to 50 miles
      }
    }
    
    // Refocus on input after selection
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleDownload = async () => {
    try {
      // Only use selected venues
      const venuesToExport = filteredVenues.filter(v => selectedVenues.has(v.venue_id));
      
      if (venuesToExport.length === 0) {
        toast.error('Please select at least one venue to download');
        return;
      }
      
      const response = await axios.post(
        `${API}/venues/export`,
        venuesToExport,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = selectedType !== "All Types" 
        ? `dolphin_digital_${selectedType.toLowerCase().replace(/\s+/g, '_')}.csv`
        : 'dolphin_digital_locations.csv';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded ${venuesToExport.length} venues`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  const handleEmailSend = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check if any venues are selected
    const venuesToEmail = filteredVenues.filter(v => selectedVenues.has(v.venue_id));
    
    if (venuesToEmail.length === 0) {
      toast.error('Please select at least one venue to send');
      return;
    }

    setEmailSending(true);
    
    try {
      
      const response = await axios.post(`${API}/venues/email`, {
        recipient_email: emailAddress,
        venues: venuesToEmail,
        message: emailMessage
      });
      
      toast.success(`Email sent successfully to ${emailAddress}!`);
      setShowEmailModal(false);
      setEmailAddress("");
      setEmailMessage("");
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email. Please check configuration.');
    } finally {
      setEmailSending(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedType("All Types");
    setShowRadiusFilter(false);
    setCenterLocation(null);
    setRadiusFilter(100);
    setSelectedVenues(new Set());
    
    // Remove radius circle from map
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
  };

  // Toggle venue selection
  const toggleVenueSelection = (venueId) => {
    setSelectedVenues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(venueId)) {
        newSet.delete(venueId);
      } else {
        newSet.add(venueId);
      }
      return newSet;
    });
  };

  // Select all filtered venues
  const selectAllVenues = () => {
    const allIds = new Set(filteredVenues.map(v => v.venue_id));
    setSelectedVenues(allIds);
  };

  // Deselect all venues
  const deselectAllVenues = () => {
    setSelectedVenues(new Set());
  };

  // Check if all filtered venues are selected
  const allSelected = filteredVenues.length > 0 && 
    filteredVenues.every(v => selectedVenues.has(v.venue_id));

  // Draw radius circle on map
  useEffect(() => {
    if (!googleMapRef.current || !window.google || !showRadiusFilter || !centerLocation) {
      // Remove circle if filter is disabled
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = null;
      }
      return;
    }

    // Remove existing circle
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
    }

    // Create new circle
    const circle = new window.google.maps.Circle({
      strokeColor: '#2563eb',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3b82f6',
      fillOpacity: 0.15,
      map: googleMapRef.current,
      center: { lat: centerLocation.lat, lng: centerLocation.lng },
      radius: radiusFilter * 1609.34, // Convert miles to meters
    });

    radiusCircleRef.current = circle;

    // Zoom to fit circle
    const bounds = circle.getBounds();
    if (bounds) {
      googleMapRef.current.fitBounds(bounds);
    }
  }, [showRadiusFilter, centerLocation, radiusFilter, mapLoaded]);

  const venueTypes = ["All Types", ...Object.keys(VENUE_TYPE_COLORS)];
  const hasActiveFilters = searchQuery || selectedType !== "All Types" || showRadiusFilter;

  return (
    <div className="app-container" data-testid="map-explorer-container">
      <div className="map-section" data-testid="map-section">
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} data-testid="google-map"></div>
        
        {(loading) && (
          <div className="map-overlay">
            <div className="flex items-center gap-3">
              <Loader2 className="animate-spin" size={32} />
              <div>
                <p className="font-semibold text-lg">{loading ? 'Loading venues...' : 'Rendering markers...'}</p>
                { <p className="text-sm text-gray-600">Processing {filteredVenues.length} locations</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sidebar-section" data-testid="sidebar-section">
        <div className="sidebar-header" data-testid="sidebar-header">
          <div className="logo-container">
            <img 
              src="venue-logo.png" 
              alt="Venue Explorer" 
              className="brand-logo"
              style={{ maxWidth: '150px', height: 'auto' }}
            />
          </div>
          <div className="app-name-container">
            <MapPin className="app-icon" size={16} />
            <p className="subtitle">Venue Explorer</p>
          </div>
        </div>

        <div className="filters-section">
          <div className="search-container" data-testid="search-container" style={{ position: 'relative' }}>
            <Search className="search-icon" size={18} />
            <Input
              ref={searchInputRef}
              data-testid="location-search-input"
              type="text"
              placeholder="Search location, city, or ZIP..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="search-input"
            />
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="search-suggestions" data-testid="search-suggestions">
                {searchSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      handleSuggestionClick(suggestion);
                    }}
                    data-testid={`suggestion-${idx}`}
                  >
                    <span className="suggestion-icon">
                      {suggestion.type === 'store' && <Building2 size={14} />}
                      {suggestion.type === 'city' && <MapPin size={14} />}
                      {suggestion.type === 'state' && <MapPin size={14} />}
                      {suggestion.type === 'zipcode' && <Search size={14} />}
                    </span>
                    <div className="suggestion-content">
                      <span className="suggestion-text">{suggestion.display}</span>
                      {suggestion.subdisplay && (
                        <span className="suggestion-subdisplay">{suggestion.subdisplay}</span>
                      )}
                    </div>
                    <span className="suggestion-type">{suggestion.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="filter-container" data-testid="filter-container">
            <label className="filter-label" data-testid="filter-label">
              <Filter size={16} className="inline mr-1" />
              Venue Type
            </label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger data-testid="venue-type-select" className="filter-select">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent data-testid="venue-type-options">
                {venueTypes.map(type => (
                  <SelectItem key={type} value={type} data-testid={`venue-type-option-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Radius Filter Slider */}
          {showRadiusFilter && centerLocation && (
            <div className="radius-filter-container" data-testid="radius-filter-container">
              <div className="radius-filter-header">
                <label className="filter-label">
                  <MapPin size={16} className="inline mr-1" />
                  Distance from {centerLocation.name}
                </label>
                <span className="radius-value">{radiusFilter} miles</span>
              </div>
              <input
                type="range"
                min="1"
                max="500"
                value={radiusFilter}
                onChange={(e) => setRadiusFilter(Number(e.target.value))}
                className="radius-slider"
                data-testid="radius-slider"
              />
              <div className="radius-labels">
                <span>1 mi</span>
                <span>100 mi</span>
                <span>500 mi</span>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <Button 
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="clear-filters-btn"
              data-testid="clear-filters-btn"
            >
              <X size={16} className="mr-1" />
              Clear Filters
            </Button>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              data-testid="download-csv-button"
              onClick={handleDownload}
              className="download-button"
              disabled={selectedVenues.size === 0}
              style={{ flex: 1 }}
            >
              <Download size={18} className="mr-2" />
              Download ({selectedVenues.size})
            </Button>
            
            <Button 
              data-testid="send-email-button"
              onClick={() => setShowEmailModal(true)}
              className="send-button"
              disabled={selectedVenues.size === 0}
              style={{ flex: 1 }}
            >
              <Send size={18} className="mr-2" />
              Send ({selectedVenues.size})
            </Button>
          </div>
        </div>

        <div className="venue-list-container" data-testid="venue-list-container">
          <div className="venue-selection-header">
            <div className="venue-count" data-testid="venue-count">
              {filteredVenues.length} {filteredVenues.length === 1 ? 'Venue' : 'Venues'}
              {hasActiveFilters && <span className="filtered-indicator">• Filtered</span>}
              {selectedVenues.size > 0 && (
                <span className="selected-indicator"> • {selectedVenues.size} selected</span>
              )}
            </div>
            
            {filteredVenues.length > 0 && (
              <Button 
                variant={allSelected ? "default" : "outline"}
                size="sm"
                onClick={() => allSelected ? deselectAllVenues() : selectAllVenues()}
                className={allSelected ? "deselect-all-btn" : "select-all-btn"}
                data-testid="select-all-btn"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
          
          <div className="venue-list" data-testid="venue-list">
            {filteredVenues.slice(0, 50).map((venue, idx) => (
              <Card 
                key={idx} 
                className={`venue-card ${selectedVenues.has(venue.venue_id) ? 'venue-card-selected' : ''}`}
                data-testid={`venue-card-${idx}`}
              >
                <div className="venue-checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="venue-checkbox"
                    checked={selectedVenues.has(venue.venue_id)}
                    onChange={() => toggleVenueSelection(venue.venue_id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`venue-checkbox-${idx}`}
                  />
                </div>
                {venue.image_url && (
                  <div 
                    className="venue-image-container"
                    onClick={(e) => handleVenueDetails(venue, e)}
                    style={{ cursor: 'pointer', position: 'relative' }}
                  >
                    <img 
                      src={venue.image_url} 
                      alt={venue.venue_name}
                      className="venue-image"
                      data-testid={`venue-image-${idx}`}
                      loading="lazy"
                    />
                    <div className="image-overlay">
                      <ExternalLink size={24} color="white" />
                    </div>
                  </div>
                )}
                <div className="venue-details">
                  <h3 className="venue-name" data-testid={`venue-name-${idx}`}>
                    {venue.store_name || venue.venue_name}
                  </h3>
                  <p className="venue-address" data-testid={`venue-address-${idx}`}>
                    {venue.city}, {venue.state} {venue.zipcode}
                  </p>
                  <Badge 
                    className="venue-badge"
                    data-testid={`venue-badge-${idx}`}
                    style={{ 
                      backgroundColor: VENUE_TYPE_COLORS[venue.venue_type] || VENUE_TYPE_COLORS["Other"],
                      color: '#ffffff'
                    }}
                  >
                    {venue.venue_type}
                  </Badge>
                  <div className="venue-actions">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Center the map on the venue
                        if (googleMapRef.current) {
                          const maxZoom = googleMapRef.current.maxZoom ?? 21;
                          googleMapRef.current.setCenter({ lat: venue.latitude, lng: venue.longitude });
                          googleMapRef.current.setZoom(Math.min(15, maxZoom));

                          // Wait for map to be idle, then open info window
                          const listener = googleMapRef.current.addListener("idle", () => {
                            const key = `v_${venue.venue_id}`;
                            const marker = renderedMarkersRef.current.get(key);
                            if (marker?.content) {
                              marker.content.click();
                            }
                            if (listener) listener.remove();
                          });
                        }
                      }}
                      className="view-map-btn"
                      data-testid={`view-map-btn-${idx}`}
                    >
                      <MapPinned size={16} className="mr-1" />
                      View on Map
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => handleVenueDetails(venue, e)}
                      className="more-details-btn"
                      data-testid={`more-details-btn-${idx}`}
                    >
                      More Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {filteredVenues.length > 50 && (
              <div className="load-more-indicator">
                Showing 50 of {filteredVenues.length} venues. Use filters to narrow results.
              </div>
            )}
          </div>
        </div>
      </div>

      <VenueDetailsModal
        open={showDetailsModal}
        onOpenChange={(open) => {
          setShowDetailsModal(open);
          if (!open) {
          }
        }}
        selectedVenue={selectedVenue}
        venueTypeColors={VENUE_TYPE_COLORS}
        onViewOnMap={(venue) => {
          // Close the modal first
          setShowDetailsModal(false);

          // Center the map on the venue
          if (googleMapRef.current) {
            const maxZoom = googleMapRef.current.maxZoom ?? 21;
            googleMapRef.current.setCenter({ lat: venue.latitude, lng: venue.longitude });
            googleMapRef.current.setZoom(Math.min(15, maxZoom));

            // Wait for map to be idle, then open info window
            const listener = googleMapRef.current.addListener("idle", () => {
              const key = `v_${venue.venue_id}`;
              const marker = renderedMarkersRef.current.get(key);
              if (marker?.content) {
                marker.content.click();
              }
              if (listener) listener.remove();
            });
          }
        }}
      />

      <EmailModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        selectedCount={selectedVenues.size}
        emailAddress={emailAddress}
        setEmailAddress={setEmailAddress}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        emailSending={emailSending}
        onSend={handleEmailSend}
        onCancel={() => {
          setShowEmailModal(false);
          setEmailAddress("");
          setEmailMessage("");
        }}
      />

    </div>
  );
}

export default AppOld;